import { type ChainId } from "@umami/chains";

import { bscConfig } from "../../chains/src/providers/evm/bsc/config";
import { ethereumConfig } from "../../chains/src/providers/evm/ethereum/config";
import { etherlinkConfig } from "../../chains/src/providers/evm/etherlink/config";

export const CHAIN_ID_MAPPING: Record<ChainId, string> = {
  ethereum: "1",
  bsc: "56",
  etherlink: "8453",
  tezos: "1729",
  bitcoin: "0",
} as const;

export const LIFI_CHAIN_ID_MAPPING: Record<ChainId, string> = {
  ethereum: "1",
  bsc: "56",
  etherlink: "42793",
  tezos: "1729",
  bitcoin: "0",
} as const;

export type ChainIdValue = (typeof CHAIN_ID_MAPPING)[ChainId];

export const ierc20Abi = [
  "function decimals() view returns (uint8)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address to, uint256 amount) returns (bool)",
];

export type Network = "shadownet" | "mainnet";

const NETWORK_URLS = {
  // Mainnet
  ethereum: ethereumConfig.mainnet_infura.rpcUrl,
  etherlink: etherlinkConfig.mainnet.rpcUrl,
  bsc: bscConfig.mainnet.rpcUrl,
  // Testnet
  sepolia: ethereumConfig.testnet_infura_sepolia.rpcUrl,
  etherlinkTestnet: etherlinkConfig.testnet.rpcUrl,
  bscTestnet: bscConfig.testnet.rpcUrl,
};

// LayerZero Endpoint IDs
const ENDPOINT_IDS = {
  // Mainnet
  ethereum: "101",
  etherlink: "292",
  bsc: "102",
  // Testnet
  sepolia: "10161",
  etherlinkTestnet: "10239",
  bscTestnet: "10102",
};

const BRIDGE_ADDRESSES = {
  // Mainnet
  ethereum: "0x1f8E735f424B7A49A885571A2fA104E8C13C26c7",
  etherlink: "0x1f8E735f424B7A49A885571A2fA104E8C13C26c7",
  bsc: "0x1f8E735f424B7A49A885571A2fA104E8C13C26c7",
  // Testnet
  sepolia: "0x1687412b4Cb0f0753BA3919849e729E1bbeC8345",
  etherlinkTestnet: "0x137d4e9C2431A3DCBa6e615E9438F2c558353a17",
  bscTestnet: "0x544d75a99916CA53394fFc7E0f38c4FE4d08d11b",
};

type TokenAddresses = {
  [key: string]: {
    [key: string]: string;
  };
};

const TOKEN_ADDRESSES: TokenAddresses = {
  ETH: {
    // Ethereum
    ethereum: "0x0000000000000000000000000000000000000000",
    sepolia: "0x0000000000000000000000000000000000000000",
  },
  WETH: {
    // Ethereum
    ethereum: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
    sepolia: "0x7b79995e5f793A07Bc00c21412e50Ecae098E7f9",
    // Etherlink
    etherlink: "0xfc24f770F94edBca6D6f885E12d4317320BcB401",
    etherlinkTestnet: "0x86932ff467A7e055d679F7578A0A4F96Be287861",
  },
  XTZ: {
    // Tezos
    tezos: "",
    tezosTestnet: "",
    etherlink: "0x0000000000000000000000000000000000000000",
    etherlinkTestnet: "0x0000000000000000000000000000000000000000",
    tezosMainnet: "",
  },
  WXTZ: {
    etherlink: "0xc9B53AB2679f573e480d01e0f49e2B5CFB7a3EAb",
  },
  USDC: {
    // Ethereum
    ethereum: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
    sepolia: "0x6f14C02Fc1F78322cFd7d707aB90f18baD3B54f5",
    // Etherlink
    etherlink: "0x796Ea11Fa2dD751eD01b53C372fFDB4AAa8f00F9",
    etherlinkTestnet: "0xc92eaA8bb3B267C3c2553e1596807c7B847192A1",
    // BSC
    bsc: "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d",
    bscTestnet: "0x89A44C4fa11630E11425c177cE08828179A249A6",
  },
  USDT: {
    // Ethereum
    ethereum: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
    sepolia: "0xaa8e23fb1079ea71e0a56f48a2aa51851d8433d0",
    // Etherlink
    etherlink: "0x2C03058C8AFC06713be23e58D2febC8337dbfE6A",
    etherlinkTestnet: "0xf7f007dc8Cb507e25e8b7dbDa600c07FdCF9A75B",
  },
} as const;

export type BridgeNetwork = "shadownet" | "mainnet";

export interface BridgeConfig {
  // Existing EVM bridge config
  ethereumRpcUrl: string;
  ethereumLayerZeroChainId: string;
  originalTokenBridgeContractAddress: string;
  etherlinkRpcUrl: string;
  etherlinkLayerZeroChainId: string;
  wrappedTokenBridgeContractAddress: string;
  bscRpcUrl: string;
  bscLayerZeroChainId: string;
  bscBridgeContractAddress: string;
  ethereumWethAddress: string;

  // Tezos-Etherlink bridge config
  dipDupBaseUrl: string;
  dipDupWebSocketUrl: string;
  tzktApiBaseUrl: string;
  tezosSmartRollupAddress: string;
  tezosXtzTokenAddress: string;
  etherlinkXtzTokenAddress: string;
}

export const config: Record<BridgeNetwork, BridgeConfig> = {
  shadownet: {
    // Existing EVM bridge config
    ethereumRpcUrl: NETWORK_URLS.sepolia,
    ethereumLayerZeroChainId: ENDPOINT_IDS.sepolia,
    originalTokenBridgeContractAddress: BRIDGE_ADDRESSES.sepolia,
    etherlinkRpcUrl: NETWORK_URLS.etherlinkTestnet,
    etherlinkLayerZeroChainId: ENDPOINT_IDS.etherlinkTestnet,
    wrappedTokenBridgeContractAddress: BRIDGE_ADDRESSES.etherlinkTestnet,
    bscRpcUrl: NETWORK_URLS.bscTestnet,
    bscLayerZeroChainId: ENDPOINT_IDS.bscTestnet,
    bscBridgeContractAddress: BRIDGE_ADDRESSES.bscTestnet,
    ethereumWethAddress: TOKEN_ADDRESSES.WETH.sepolia,

    // Tezos-Etherlink bridge config
    dipDupBaseUrl: "https://testnet.bridge.indexer.etherlink.com",
    dipDupWebSocketUrl: "wss://testnet.bridge.indexer.etherlink.com",
    tzktApiBaseUrl: "https://api.shadownet.tzkt.io",
    tezosSmartRollupAddress: "sr18wx6ezkeRjt1SZSeZ2UQzQN3Uc3YLMLqg",
    tezosXtzTokenAddress: TOKEN_ADDRESSES.XTZ.tezos,
    etherlinkXtzTokenAddress: TOKEN_ADDRESSES.XTZ.etherlinkTestnet,
  },
  mainnet: {
    // Existing EVM bridge config
    ethereumRpcUrl: NETWORK_URLS.ethereum,
    ethereumLayerZeroChainId: ENDPOINT_IDS.ethereum,
    originalTokenBridgeContractAddress: BRIDGE_ADDRESSES.ethereum,
    etherlinkRpcUrl: NETWORK_URLS.etherlink,
    etherlinkLayerZeroChainId: ENDPOINT_IDS.etherlink,
    wrappedTokenBridgeContractAddress: BRIDGE_ADDRESSES.etherlink,
    bscRpcUrl: NETWORK_URLS.bsc,
    bscLayerZeroChainId: ENDPOINT_IDS.bsc,
    bscBridgeContractAddress: BRIDGE_ADDRESSES.bsc,
    ethereumWethAddress: TOKEN_ADDRESSES.WETH.ethereum,

    // Tezos-Etherlink bridge config
    dipDupBaseUrl: "https://bridge.indexer.etherlink.com",
    dipDupWebSocketUrl: "wss://bridge.indexer.etherlink.com",
    tzktApiBaseUrl: "https://api.tzkt.io",
    tezosSmartRollupAddress: "sr1Ghq66tYK9y3r8CC1Tf8i8m5nxh8nTvZEf",
    tezosXtzTokenAddress: TOKEN_ADDRESSES.XTZ.tezosMainnet,
    etherlinkXtzTokenAddress: TOKEN_ADDRESSES.XTZ.etherlink,
  },
};

export function getTokenAddress(
  tokenSymbol: string,
  network: BridgeNetwork,
  chain: ChainId
): string | undefined {
  const token = TOKEN_ADDRESSES[tokenSymbol];

  if (chain === "ethereum") {
    return network === "mainnet" ? token.ethereum : token.sepolia;
  }

  if (chain === "etherlink") {
    return network === "mainnet" ? token.etherlink : token.etherlinkTestnet;
  }

  if (chain === "bsc") {
    return network === "mainnet" ? token.bsc : token.bscTestnet;
  }

  return undefined;
}
